import { useEffect, useRef, useState } from "react";
import type { FreqPoint } from "../types/dashboard";

const WINDOW_MS = 10_000;
/** Chart advances on a fixed clock; MQTT only refreshes held A/B/C values. */
export const SPARKLINE_TICK_MS = 50;
const MAX_FRAMES = Math.ceil(WINDOW_MS / SPARKLINE_TICK_MS) + 20;

/** Matches freq-service main_sync_threshold_hz (30 mHz). */
export const MAIN_C_SYNC_THRESHOLD_HZ = 0.03;
/** Matches freq-service main_max_sample_age_ms. */
export const MAIN_C_STALE_MS = 500;

export type SparklineFrame = {
  ts_ms: number;
  main_A: number;
  main_B: number;
  main_C: number | null;
};

export type MeterSparklineSeries = {
  main_A: FreqPoint[];
  main_B: FreqPoint[];
  main_C: FreqPoint[];
};

export type MeterLiveHz = {
  main_A: number | null;
  main_B: number | null;
  main_C: number | null;
};

export type MeterLastTs = {
  main_A: number | null;
  main_B: number | null;
  main_C: number | null;
};

export type MeterSparklineState = {
  frames: SparklineFrame[];
  series: MeterSparklineSeries;
  /** Most recent sample time from main_A or main_B. */
  lastSampleTsMs: number | null;
  lastTs: MeterLastTs;
  liveHz: MeterLiveHz;
};

function trimWindow(frames: SparklineFrame[]): SparklineFrame[] {
  if (!frames.length) return [];
  const windowEnd = frames[frames.length - 1]!.ts_ms;
  const cutoff = windowEnd - WINDOW_MS;
  return frames.filter((f) => f.ts_ms >= cutoff).slice(-MAX_FRAMES);
}

function framesToSeries(frames: SparklineFrame[]): MeterSparklineSeries {
  return {
    main_A: frames.map((f) => ({ ts_ms: f.ts_ms, freq_hz: f.main_A, raw_u16: 0 })),
    main_B: frames.map((f) => ({ ts_ms: f.ts_ms, freq_hz: f.main_B, raw_u16: 0 })),
    main_C: frames
      .filter((f) => f.main_C != null)
      .map((f) => ({ ts_ms: f.ts_ms, freq_hz: f.main_C!, raw_u16: 0 })),
  };
}

export function medianAb(a: number, b: number) {
  return (a + b) / 2;
}

export type MainCConsensus = "agree" | "off" | "stale" | "missing";

export function meterSampleOnline(lastTs: number | null, now = Date.now()): boolean {
  return lastTs != null && now - lastTs <= MAIN_C_STALE_MS;
}

/** e.g. "2+1" when A+B and backup C are publishing; "2" when C is offline. */
export function formatMainMeterOnlineCount(lastTs: MeterLastTs, now = Date.now()): string | null {
  const a = meterSampleOnline(lastTs.main_A, now);
  const b = meterSampleOnline(lastTs.main_B, now);
  const c = meterSampleOnline(lastTs.main_C, now);
  const primary = (a ? 1 : 0) + (b ? 1 : 0);
  if (primary === 0 && !c) return null;
  return c ? `${primary}+1` : String(primary);
}

export function mainCConsensus(
  c: number | null,
  a: number | null,
  b: number | null,
  lastTsC: number | null,
  now = Date.now(),
): MainCConsensus {
  if (lastTsC == null || now - lastTsC > MAIN_C_STALE_MS) return "stale";
  if (c == null || a == null || b == null) return "missing";
  return Math.abs(c - medianAb(a, b)) <= MAIN_C_SYNC_THRESHOLD_HZ ? "agree" : "off";
}

/**
 * Rolling 10s sparkline for main_A / main_B / main_C from ext/freq/meter/main.
 * A+B drive the chart clock; C is optional backup (sample-and-hold when present).
 */
export function useFreqSparkline(active: boolean): MeterSparklineState {
  const [frames, setFrames] = useState<SparklineFrame[]>([]);
  const [series, setSeries] = useState<MeterSparklineSeries>({
    main_A: [],
    main_B: [],
    main_C: [],
  });
  const [lastSampleTsMs, setLastSampleTsMs] = useState<number | null>(null);
  const [lastTs, setLastTs] = useState<MeterLastTs>({
    main_A: null,
    main_B: null,
    main_C: null,
  });
  const [liveHz, setLiveHz] = useState<MeterLiveHz>({
    main_A: null,
    main_B: null,
    main_C: null,
  });
  const heldRef = useRef<{ main_A: number | null; main_B: number | null; main_C: number | null }>({
    main_A: null,
    main_B: null,
    main_C: null,
  });
  const lastTsRef = useRef<MeterLastTs>({
    main_A: null,
    main_B: null,
    main_C: null,
  });
  const framesRef = useRef<SparklineFrame[]>([]);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!active) return;

    const tick = () => {
      const held = heldRef.current;
      if (held.main_A == null || held.main_B == null) return;

      const now = Date.now();
      const prev = framesRef.current[framesRef.current.length - 1];
      if (prev && now <= prev.ts_ms) return;

      framesRef.current = trimWindow([
        ...framesRef.current,
        {
          ts_ms: now,
          main_A: held.main_A,
          main_B: held.main_B,
          main_C: held.main_C,
        },
      ]);
      const trimmed = framesRef.current;
      setFrames(trimmed);
      setSeries(framesToSeries(trimmed));
    };

    const intervalId = setInterval(tick, SPARKLINE_TICK_MS);
    return () => clearInterval(intervalId);
  }, [active]);

  useEffect(() => {
    if (!active) {
      heldRef.current = { main_A: null, main_B: null, main_C: null };
      lastTsRef.current = { main_A: null, main_B: null, main_C: null };
      framesRef.current = [];
      setFrames([]);
      setSeries({ main_A: [], main_B: [], main_C: [] });
      setLastSampleTsMs(null);
      setLastTs({ main_A: null, main_B: null, main_C: null });
      setLiveHz({ main_A: null, main_B: null, main_C: null });
      esRef.current?.close();
      esRef.current = null;
      return;
    }

    const es = new EventSource("/api/freq/meters/live");
    esRef.current = es;

    es.addEventListener("meter", (ev) => {
      try {
        const data = JSON.parse(ev.data) as {
          meter_id?: string;
          ts_ms?: number;
          freq_hz?: number;
        };
        const meter_id = data.meter_id;
        const ts_ms = Number(data.ts_ms);
        const freq_hz = Number(data.freq_hz);
        if (meter_id !== "main_A" && meter_id !== "main_B" && meter_id !== "main_C") return;
        if (!Number.isFinite(ts_ms) || !Number.isFinite(freq_hz)) return;

        const held = heldRef.current;
        const ts = lastTsRef.current;
        if (meter_id === "main_A") {
          held.main_A = freq_hz;
          ts.main_A = ts_ms;
        } else if (meter_id === "main_B") {
          held.main_B = freq_hz;
          ts.main_B = ts_ms;
        } else {
          held.main_C = freq_hz;
          ts.main_C = ts_ms;
        }

        setLiveHz({ main_A: held.main_A, main_B: held.main_B, main_C: held.main_C });
        setLastTs({ ...ts });

        const newestTs = Math.max(ts.main_A ?? 0, ts.main_B ?? 0);
        if (newestTs > 0) setLastSampleTsMs(newestTs);
      } catch {
        /* ignore */
      }
    });

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [active]);

  return { frames, series, lastSampleTsMs, lastTs, liveHz };
}
